import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { StarIcon, MapPinIcon, BookIcon, HeadphonesIcon, DollarSignIcon, CheckCircleIcon, ClockIcon, MessageSquareIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import SaveToFavorites from '../UI/SaveToFavorites';
import { useUserRole } from '../../context/UserRoleContext';
export interface ProfileCardData {
  id: string;
  name: string;
  image: string;
  role: string;
  variant: 'author' | 'narrator' | 'creative' | 'service';
  rating: number;
  reviewCount: number;
  specialties: string[];
  available?: boolean;
  availabilityLabel?: string;
  startingPrice?: string;
  portfolioThumbnails?: string[];
  verified?: boolean;
  location?: string;
  profileUrl: string;
  bookCount?: number;
  audiobookCount?: number;
  bio?: string;
  featured?: boolean;
}
interface ProfileCardProps {
  data: ProfileCardData;
  viewMode: 'grid' | 'list';
}
export const ProfileCard: React.FC<ProfileCardProps> = ({
  data,
  viewMode
}) => {
  const dk = useDarkClasses();
  const [isExpanded, setIsExpanded] = useState(false);
  const {
    isReader
  } = useUserRole();
  // Priority keywords for content-sensitive tags that should always be visible
  const PRIORITY_KEYWORDS = ['dark', 'trigger', 'content warning', 'mature', 'explicit', 'taboo', 'dubcon', 'noncon', 'cnc', 'dark romance', 'erotic', 'graphic', 'sensitive', 'adult', 'spicy', 'steamy', '18+'];
  // Sort specialties so content-flagged tags surface first
  const getPrioritizedSpecialties = (specs: string[]) => {
    const isPriority = (tag: string) => {
      const lower = tag.toLowerCase();
      return PRIORITY_KEYWORDS.some(kw => lower.includes(kw));
    };
    const priority = specs.filter(isPriority);
    const rest = specs.filter(s => !isPriority(s));
    return [...priority, ...rest];
  };
  const prioritizedSpecialties = getPrioritizedSpecialties(data.specialties);
  // Variant-based styles
  const getHeaderGradient = () => {
    switch (data.variant) {
      case 'author':
        return dk.isDark ? 'bg-gradient-to-br from-indigo-900/40 to-indigo-900/40' : 'bg-gradient-to-br from-indigo-50 to-indigo-50';
      case 'narrator':
        return dk.isDark ? 'bg-gradient-to-br from-amber-900/40 to-orange-900/40' : 'bg-gradient-to-br from-amber-50 to-orange-50';
      case 'creative':
        return dk.isDark ? 'bg-gradient-to-br from-purple-900/40 to-pink-900/40' : 'bg-gradient-to-br from-purple-50 to-pink-50';
      case 'service':
        return dk.isDark ? 'bg-gradient-to-br from-purple-900/40 to-indigo-900/40' : 'bg-gradient-to-br from-purple-50 to-indigo-50';
      default:
        return dk.isDark ? 'bg-gray-800' : 'bg-gray-100';
    }
  };
  const getSpecialtyBadgeStyle = () => {
    switch (data.variant) {
      case 'author':
        return dk.isDark ? 'bg-indigo-900/30 text-indigo-300' : 'bg-indigo-50 text-indigo-700';
      case 'narrator':
        return dk.isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-50 text-amber-700';
      case 'creative':
        return dk.isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-50 text-purple-700';
      case 'service':
        return dk.isDark ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-50 text-purple-700';
      default:
        return dk.isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700';
    }
  };
  const getAvailabilityColor = () => {
    if (!data.availabilityLabel) return 'bg-gray-400';
    const label = data.availabilityLabel.toLowerCase();
    if (label.includes('open') || label.includes('available')) return 'bg-green-500';
    if (label.includes('waitlist') || label.includes('limited') || label.includes('soon')) return 'bg-yellow-500';
    return 'bg-red-500';
  };
  const getButtonColor = () => {
    switch (data.variant) {
      case 'author':
      case 'creative':
      case 'service':
        return 'bg-purple-600 hover:bg-purple-700';
      case 'narrator':
        return 'bg-amber-600 hover:bg-amber-700';
      default:
        return 'bg-blue-600 hover:bg-blue-700';
    }
  };
  const getLinkColor = () => {
    switch (data.variant) {
      case 'author':
      case 'creative':
      case 'service':
        return 'text-purple-600 hover:text-purple-700 dark:text-purple-400 dark:hover:text-purple-300';
      case 'narrator':
        return 'text-amber-600 hover:text-amber-700 dark:text-amber-400 dark:hover:text-amber-300';
      default:
        return 'text-blue-600 hover:text-blue-700';
    }
  };
  const getPriceBadgeStyle = () => {
    switch (data.variant) {
      case 'narrator':
        return dk.isDark ? 'bg-amber-900/60 text-amber-200 border-amber-700/50' : 'bg-amber-600/80 text-white border-amber-500/30';
      case 'creative':
        return dk.isDark ? 'bg-purple-900/60 text-purple-200 border-purple-700/50' : 'bg-purple-600/80 text-white border-purple-500/30';
      case 'service':
        return dk.isDark ? 'bg-indigo-900/60 text-indigo-200 border-indigo-700/50' : 'bg-indigo-600/80 text-white border-indigo-500/30';
      default:
        return dk.isDark ? 'bg-indigo-900/60 text-indigo-200 border-indigo-700/50' : 'bg-indigo-600/80 text-white border-indigo-500/30';
    }
  };
  if (viewMode === 'list') {
    return <div className={`rounded-xl overflow-hidden border transition-all ${dk.isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`} data-id="element-1364" data-mp="2015tmkwvqtxc">
        {/* Collapsed Row */}
        <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-id="element-1365" data-mp="29zlkcmrzw16b">
          {/* Thumbnail */}
          <div className="relative flex-shrink-0" data-id="element-1366" data-mp="252d2knardz5l">
            <img src={data.image} alt={data.name} className={`w-14 h-14 rounded-xl object-cover border-2 ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-1367" data-mp="12nw0lwl4vyfc" />
          </div>

          {/* Core Info */}
          <div className="flex-1 min-w-0" data-id="element-1368" data-mp="1bn0yr1ss9pqz">
            <div className="flex items-center flex-wrap gap-2 mb-0.5" data-id="element-1369" data-mp="1gfaks6qs25nz">
              <h3 className={`text-base font-bold ${dk.isDark ? 'text-gray-100' : 'text-gray-900'}`} data-id="element-1370" data-mp="1gglqwd7p3pi9">
                {data.name}
              </h3>
              {data.verified && <CheckCircleIcon className={`h-3.5 w-3.5 ${data.variant === 'narrator' ? 'text-amber-500' : 'text-purple-500'}`} aria-label="Verified" data-id="element-1371" data-mp="1qfb803rrp5xg" />}
              <span className={`text-sm ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-1372" data-mp="1an72fems3w">
                ·
              </span>
              <span className={`text-sm ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1373" data-mp="5283f9gutc2r">
                {data.role}
              </span>
            </div>
            <div className="flex items-center flex-wrap gap-3 text-sm" data-id="element-1374" data-mp="3otz7h2628rc1">
              {/* Rating */}
              <div className="flex items-center text-yellow-600 dark:text-yellow-400" data-id="element-1375" data-mp="3jwgwz7cea41x">
                <StarIcon className="h-3.5 w-3.5 fill-current mr-1" data-id="element-1376" data-mp="3eyp4ab3xedwo" />
                <span className="font-bold" data-id="element-1377" data-mp="gfql4bu4swsb">{data.rating}</span>
                <span className={`ml-1 text-xs ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-1378" data-mp="gd4bm6nok77g">
                  ({data.reviewCount})
                </span>
              </div>
              {/* Availability */}
              {data.availabilityLabel && <span className="flex items-center gap-1.5" data-id="element-1379" data-mp="2i1yozkf1hl5">
                  <span className={`w-2 h-2 rounded-full ${getAvailabilityColor()}`} data-id="element-1380" data-mp="2r8b9wdxvc2yx"></span>
                  <span className={`text-xs font-medium ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1381" data-mp="2n812qxycb6ih">
                    {data.availabilityLabel.split('·')[0].trim()}
                  </span>
                </span>}
              {/* Starting Price */}
              {data.startingPrice && <div className={`flex items-center font-medium ${dk.isDark ? 'text-green-400' : 'text-green-600'}`} data-id="element-1382" data-mp="m4qja111t1pu">
                  <DollarSignIcon className="h-3.5 w-3.5 mr-0.5" data-id="element-1383" data-mp="19vnm8oav8ooy" />
                  <span className="text-xs" data-id="element-1384" data-mp="1dud7t4625cjo">From {data.startingPrice}</span>
                </div>}
              {/* Book/Audiobook counts */}
              {data.bookCount !== undefined && <div className={`flex items-center text-xs ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1385" data-mp="150qf5mx5cpep">
                  <BookIcon className="h-3 w-3 mr-1" data-id="element-1386" data-mp="1xjdu0kljv87c" />
                  {data.bookCount} books
                </div>}
              {data.audiobookCount !== undefined && <div className={`flex items-center text-xs ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1387" data-mp="22wc7qml9siol">
                  <HeadphonesIcon className="h-3 w-3 mr-1" data-id="element-1388" data-mp="2gm5zbco5th20" />
                  {data.audiobookCount}
                </div>}
            </div>
          </div>

          {/* Expand Toggle */}
          <button onClick={() => setIsExpanded(!isExpanded)} className={`p-2 rounded-full transition-colors self-end sm:self-center ${dk.isDark ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`} aria-label={isExpanded ? 'Collapse details' : 'Expand details'} data-id="element-1389" data-mp="1nzbq5bwsm43u">
            {isExpanded ? <ChevronUpIcon className="h-5 w-5" data-id="element-1390" data-mp="39bmw0mitbk41" /> : <ChevronDownIcon className="h-5 w-5" data-id="element-1391" data-mp="39dzed0shfwhm" />}
          </button>
        </div>

        {/* Expanded Details */}
        {isExpanded && <div className={`border-t p-4 sm:p-6 ${dk.isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-100 bg-gray-50/50'}`} data-id="element-1392" data-mp="3jdt2cs4kaznt">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-1393" data-mp="39p1eav2mgzi6">
              {/* Left: Bio + Specialties + Portfolio */}
              <div className="lg:col-span-2 space-y-4" data-id="element-1394" data-mp="34p6q4za8eo0t">
                {data.bio && <div data-id="element-1395" data-mp="30owfh9bhv9fm">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1396" data-mp="30q7kfcgoen8b">
                      About
                    </h4>
                    <p className={`text-sm leading-relaxed ${dk.isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-1397" data-mp="2qs9wyr6n4ugr">
                      {data.bio}
                    </p>
                  </div>}

                {data.specialties.length > 0 && <div data-id="element-1398" data-mp="sgzqiaf4g5wy">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1399" data-mp="shj5pjde0g7a">
                      Specialties
                    </h4>
                    <div className="flex flex-wrap gap-2" data-id="element-1400" data-mp="119uxp9o5gbpd">
                      {data.specialties.map((spec, idx) => <span key={idx} className={`text-xs px-2.5 py-1 rounded-md font-medium ${getSpecialtyBadgeStyle()}`} data-id="element-1401" data-mp="16a8vzzzy87a7" data-mpi={idx}>
                          {spec}
                        </span>)}
                    </div>
                  </div>}

                {/* Portfolio Thumbnails */}
                {data.portfolioThumbnails && data.portfolioThumbnails.length > 0 && <div data-id="element-1402" data-mp="1yx3eird7gwl1">
                      <h4 className={`text-xs font-bold uppercase tracking-wider mb-2 ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1403" data-mp="1ywafkckhnbdg">
                        Portfolio
                      </h4>
                      <div className="flex gap-2" data-id="element-1404" data-mp="149lehfro4l7u">
                        {data.portfolioThumbnails.slice(0, 4).map((thumb, idx) => <div key={idx} className={`w-20 h-14 rounded-lg overflow-hidden border ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-1405" data-mp="1e8sobsz71xq7" data-mpi={idx}>
                              <img src={thumb} alt="Portfolio" className="w-full h-full object-cover" data-id="element-1406" data-mp="107zuzbkmp8oo" />
                            </div>)}
                        {data.portfolioThumbnails.length > 4 && <div className={`w-20 h-14 rounded-lg flex items-center justify-center text-xs font-medium ${dk.isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`} data-id="element-1407" data-mp="2slyvn8opo91">
                            +{data.portfolioThumbnails.length - 4} more
                          </div>}
                      </div>
                    </div>}
              </div>

              {/* Right: Location + Actions */}
              <div className={`lg:border-l pl-0 lg:pl-8 space-y-4 ${dk.isDark ? 'border-gray-700' : 'border-gray-200'}`} data-id="element-1408" data-mp="2x4w1epdofgo8">
                <div className="space-y-2" data-id="element-1409" data-mp="2o4z9mevzq1oy">
                  {data.location && <div className="flex items-center text-sm" data-id="element-1410" data-mp="2s6uka9zt9rfe">
                      <MapPinIcon className={`h-4 w-4 mr-2 ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-1411" data-mp="2s9gn5wbrbira" />

                      <span className={dk.isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-1412" data-mp="1gt8my1jssjis">
                        {data.location}
                      </span>
                    </div>}
                  {data.startingPrice && <div className="flex items-center text-sm" data-id="element-1413" data-mp="1iapdkw4audjl">
                      <DollarSignIcon className={`h-4 w-4 mr-2 text-green-500`} data-id="element-1414" data-mp="1ica4mbrenecp" />

                      <span className={dk.isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-1415" data-mp="2fw3fbg4rm0j3">
                        From {data.startingPrice}
                      </span>
                    </div>}
                </div>

                <div className="flex flex-col gap-2" data-id="element-1416" data-mp="uld6c1sj8fjx">
                  <Link to={data.profileUrl} className={`block w-full text-center py-2.5 rounded-lg text-sm font-medium transition-colors ${dk.isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1417" data-mp="uj0emwwz2qto">
                    View Profile
                  </Link>
                  <button className={`w-full text-white py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm hover:shadow flex items-center justify-center gap-2 ${getButtonColor()}`} data-id="element-1418" data-mp="3b6rn4r1xfwe4">
                    <MessageSquareIcon className="h-4 w-4" data-id="element-1419" data-mp="3kzp7z3ue5xfw" />
                    Contact
                  </button>
                </div>
              </div>
            </div>
          </div>}
      </div>;
  }
  // Grid View
  return <div className={`group relative rounded-xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-xl border ${dk.isDark ? 'bg-gray-800 border-gray-700 shadow-lg' : 'bg-white border-gray-100 shadow-md'}`} data-id="element-1420" data-mp="2rzvbjhjb5x9">
      {/* Colored Header Area */}
      <div className={`relative h-20 sm:h-28 ${getHeaderGradient()}`} data-id="element-1421" data-mp="37tqoickt6146">
        {/* Top-left: Starting Price Badge */}
        {data.startingPrice && <div className="absolute top-2 left-2 sm:top-3 sm:left-3 z-30" data-id="element-1422" data-mp="32wi9gkuyid75">
            <span className={`inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-semibold px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-full backdrop-blur-sm border ${getPriceBadgeStyle()}`} data-id="element-1423" data-mp="3gvrk750e8f3q">
              <DollarSignIcon className="h-3 w-3" data-id="element-1424" data-mp="3qr1ojze9kyko" />
              <span className="hidden sm:inline" data-id="element-1425" data-mp="3lub99eaaa70b">From </span>
              {data.startingPrice}
            </span>
          </div>}

        {/* Top-right: Save/Favourite */}
        <div className="absolute top-2 right-2 sm:top-3 sm:right-3 z-30" data-id="element-1426" data-mp="3vjl0krxazlnd">
          <SaveToFavorites itemId={data.id} size="sm" className={dk.isDark ? 'bg-white/10 backdrop-blur-sm hover:bg-white/20 text-white' : 'bg-white/60 backdrop-blur-sm hover:bg-white/80'} data-id="element-1427" data-mp="3vlpg674ke93h" />
        </div>
      </div>

      {/* Content */}
      <div className="relative px-3 pt-9 pb-4 sm:px-6 sm:pt-12 sm:pb-6" data-id="element-1428" data-mp="x0ba0gb72erl">
        {/* Avatar */}
        <img src={data.image} alt={data.name} className={`absolute -top-7 left-3 w-14 h-14 sm:-top-10 sm:left-6 sm:w-20 sm:h-20 rounded-xl object-cover shadow-lg border-2 sm:border-4 ${dk.isDark ? 'border-gray-800' : 'border-white'}`} data-id="element-1429" data-mp="i30r4lw826lu" />

        {/* Name & Role */}
        <div className="mb-2 sm:mb-3" data-id="element-1430" data-mp="e1fduzfrzqb4">
          <div className="flex items-center justify-between" data-id="element-1431" data-mp="e28fjlzvnpme">
            <h2 className={`text-base sm:text-lg font-bold line-clamp-1 ${dk.isDark ? 'text-gray-100' : 'text-gray-900'}`} data-id="element-1432" data-mp="3vi6tpj9lol4f">
              {data.name}
            </h2>
            {/* Availability Dot — vendor/author only */}
            {!isReader && data.availabilityLabel && <div className="flex items-center gap-1.5 flex-shrink-0 ml-2" title={data.availabilityLabel} data-id="element-1433" data-mp="3qh9ad3cutl1c">
                <span className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full ${getAvailabilityColor()}`} data-id="element-1434" data-mp="5tcqb1nieene"></span>
                <span className={`text-xs font-medium ${dk.isDark ? 'text-gray-400' : 'text-gray-500'} hidden sm:inline-block`} data-id="element-1435" data-mp="t89faiovhwn">
                  {data.availabilityLabel.split('·')[0].trim()}
                </span>
              </div>}
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5" data-id="element-1436" data-mp="3j77p3z7fzlev">
            <span className={`text-xs sm:text-sm ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1437" data-mp="3e8wehm9bp6vl">
              {data.role}
            </span>
            {data.verified && <CheckCircleIcon className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${data.variant === 'narrator' ? 'text-amber-500' : 'text-purple-500'}`} data-id="element-1438" data-mp="34bhzlr31sq5e" />}
          </div>
        </div>

        {/* Rating & Stats */}
        <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-4" data-id="element-1439" data-mp="1fr2pj7ihohkr">
          <div className="flex items-center bg-yellow-500/10 px-1.5 py-0.5 sm:px-2 sm:py-1 rounded text-yellow-600 dark:text-yellow-400 text-xs sm:text-sm" data-id="element-1440" data-mp="1jrwf05bky4fr">
            <StarIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 fill-current mr-1" data-id="element-1441" data-mp="1tr3ouij3vgy4" />
            <span className="font-bold" data-id="element-1442" data-mp="1tp10s41ijxw1">{data.rating}</span>
            <span className={`ml-1 text-[10px] sm:text-xs ${dk.isDark ? 'text-gray-500' : 'text-gray-500'}`} data-id="element-1443" data-mp="w4eftopzk42r">
              ({data.reviewCount})
            </span>
          </div>
          {data.bookCount !== undefined && <div className={`flex items-center text-[10px] sm:text-xs ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1444" data-mp="15zq00mk5y3hw">
              <BookIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" data-id="element-1445" data-mp="dc2gzbyjjkxd" />
              {data.bookCount}{' '}
              <span className="hidden sm:inline ml-1" data-id="element-1446" data-mp="iang18223yk8">books</span>
            </div>}
          {data.audiobookCount !== undefined && <div className={`flex items-center text-[10px] sm:text-xs ${dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-1447" data-mp="3rdzzyp4onslu">
              <HeadphonesIcon className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" data-id="element-1448" data-mp="906z505ilrf" />
              {data.audiobookCount}{' '}
              <span className="hidden sm:inline ml-1" data-id="element-1449" data-mp="560fdmkh40lp">books</span>
            </div>}
        </div>

        {/* Specialties */}
        <div className="mb-3 sm:mb-4" data-id="element-1450" data-mp="38nicp02mdp7v">
          <div className="flex flex-wrap gap-1.5 sm:gap-2" data-id="element-1451" data-mp="38ojudne559yk">
            {prioritizedSpecialties.slice(0, 2).map((spec, idx) => <span key={idx} className={`text-[10px] sm:text-xs px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md font-medium truncate max-w-[100px] sm:max-w-none ${getSpecialtyBadgeStyle()}`} data-id="element-1452" data-mp="3dkif9109g9u8" data-mpi={idx}>
                {spec}
              </span>)}
            {prioritizedSpecialties.length > 2 && <span key="spec-3" className={`hidden sm:inline-flex text-[10px] sm:text-xs px-2 py-0.5 sm:px-2.5 sm:py-1 rounded-md font-medium ${getSpecialtyBadgeStyle()}`} data-id="element-1453" data-mp="172efnb1tlqpr">
                {prioritizedSpecialties[2]}
              </span>}
            {prioritizedSpecialties.length > 3 && <span className={`text-[10px] sm:text-xs px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-md ${dk.isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`} data-id="element-1454" data-mp="t47t2r2bnd5e">
                <span className="sm:hidden" data-id="element-1455" data-mp="ja7gk44pn5mn">
                  +{prioritizedSpecialties.length - 2}
                </span>
                <span className="hidden sm:inline" data-id="element-1456" data-mp="abvrw7nemhwu">
                  +{prioritizedSpecialties.length - 3}
                </span>
              </span>}
          </div>
        </div>

        {/* Location Row (hidden on mobile) */}
        {data.location && <div className="hidden sm:flex items-center mb-4 text-xs" data-id="element-1457" data-mp="fizdd3lia3o">
            <div className={`flex items-center ${dk.isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-1458" data-mp="3mugdpk67butq">
              <MapPinIcon className="h-3 w-3 mr-1" data-id="element-1459" data-mp="3rs83o8nad6va" />
              {data.location}
            </div>
          </div>}

        {/* Portfolio Thumbnails (hidden on mobile) */}
        {data.portfolioThumbnails && data.portfolioThumbnails.length > 0 && <div className="hidden sm:flex mb-6 gap-2" data-id="element-1460" data-mp="9hcy0xyoitbn">
            {data.portfolioThumbnails.slice(0, 3).map((thumb, idx) => <div key={idx} className="w-16 h-12 rounded overflow-hidden border border-gray-200 dark:border-gray-700" data-id="element-1461" data-mp="eemw1204wdb3" data-mpi={idx}>
                <img src={thumb} alt="Portfolio" className="w-full h-full object-cover" data-id="element-1462" data-mp="4ojugdey5r15" />
              </div>)}
          </div>}

        {/* Actions */}
        <div className="flex gap-2 sm:gap-3 mt-auto" data-id="element-1463" data-mp="x80lwsozptju">
          <Link to={data.profileUrl} className={`flex-1 text-center py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors ${dk.isDark ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`} data-id="element-1464" data-mp="2hv9oqhggdf8w">
            View Profile
          </Link>
          <button className={`hidden sm:flex flex-1 text-white py-2 rounded-lg text-sm font-medium transition-colors shadow-sm hover:shadow items-center justify-center gap-2 ${getButtonColor()}`} data-id="element-1465" data-mp="2mntd89gohush">
            <MessageSquareIcon className="h-4 w-4" data-id="element-1466" data-mp="1u0qjrvqv0a93" />
            Contact
          </button>
        </div>
      </div>

      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-black/90 backdrop-blur-sm rounded-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col p-4 sm:p-6 text-white z-20 pointer-events-none" data-id="element-1467" data-mp="23vigkxcmat2t">
        <div className="mb-2" data-id="element-1468" data-mp="3faxblvl9nava">
          <h3 className="font-bold text-base sm:text-lg line-clamp-1 pr-8" data-id="element-1469" data-mp="3fdt8rhpahvio">
            {data.name}
          </h3>
          <div className="flex items-center gap-2 mt-1" data-id="element-1470" data-mp="1ttohy38jom0r">
            <div className="flex items-center text-yellow-400 text-xs" data-id="element-1471" data-mp="1tuhjmpsnclc1">
              <StarIcon className="h-3 w-3 fill-current mr-0.5" data-id="element-1472" data-mp="1xv09wfew5jc4" />
              <span className="font-bold" data-id="element-1473" data-mp="22ucwr05riqep">{data.rating}</span>
              <span className="text-white/50 ml-1" data-id="element-1474" data-mp="22vxs2rzwiddm">({data.reviewCount})</span>
            </div>
            {data.bookCount !== undefined && <span className="text-white/50 text-xs flex items-center" data-id="element-1475" data-mp="2csk893ha8g9b">
                <BookIcon className="h-3 w-3 mr-0.5" data-id="element-1476" data-mp="2cqfxq2ovoj02" />
                {data.bookCount} books
              </span>}
          </div>
        </div>

        <p className="text-xs sm:text-sm text-gray-300 mb-4 line-clamp-4 sm:line-clamp-5 flex-grow" data-id="element-1477" data-mp="2ljsyfqt6oowp">
          {data.bio || `Check out ${data.name}'s profile to learn more about their work and services.`}
        </p>

        <div className="mt-auto" data-id="element-1478" data-mp="2vjbgu2tg42mn">
          {prioritizedSpecialties.length > 0 && <div className="flex flex-wrap gap-1.5" data-id="element-1479" data-mp="35nryn1qhjtt3">
              {prioritizedSpecialties.slice(0, 4).map((spec, idx) => <span key={idx} className="text-[10px] sm:text-xs px-2 py-0.5 rounded bg-white/10 text-white/90 border border-white/10" data-id="element-1480" data-mp="30pyro6hcyvrr" data-mpi={idx}>
                  {spec}
                </span>)}
              {prioritizedSpecialties.length > 4 && <span className="text-[10px] sm:text-xs px-2 py-0.5 rounded bg-white/10 text-white/60" data-id="element-1481" data-mp="3ofb0344v2zt0">
                  +{prioritizedSpecialties.length - 4}
                </span>}
            </div>}
        </div>
      </div>
    </div>;
};